import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { formatRelativeTime, entryImageSrc } from "../lib/media";
import { useRouteBase } from "../context/ApiBaseContext";

/**
 * Iter-154c — Fullscreen continuity viewer.
 *
 * Calm, visual-first overlay. Renders a single image at object-contain
 * within the viewport; lets the user step / swipe between adjacent
 * images in the SAME gallery list (e.g. all of Felix's continuity
 * stills, in order). Mounts as a fixed overlay so navigation state
 * is never lost — the operator stays in Gallery.
 *
 * Props
 *   open          boolean
 *   images        array of entry rows (each with image_url / thumbnail_url etc.)
 *   index         current index into `images`
 *   onClose       fn()
 *   onIndexChange fn(nextIndex)
 *
 * Touch contract (iter-154c)
 *   * Horizontal swipe → step prev/next. Threshold 40px so a flick
 *     reliably commits without misfiring scroll/zoom.
 *   * Tap on backdrop (NOT the image) → close.
 *   * ESC key → close.
 *   * Arrow keys → step prev/next.
 *
 * Future: pinch-to-zoom is intentionally NOT in this first cut. The
 * iPad workflow will need it but adding it now without proper gesture
 * libraries risks breaking the simple swipe contract. Deferred.
 */
export default function GalleryViewer({
    open,
    projectId,
    images,
    index,
    onClose,
    onIndexChange,
    galleryReturnTo,
}) {
    const navigate = useNavigate();
    const routeBase = useRouteBase();
    const [errored, setErrored] = useState(false);
    const touchRef = useRef({ startX: 0, startY: 0 });

    const current = open && images && images.length ? images[index] : null;

    // Reset error state on image change.
    useEffect(() => {
        setErrored(false);
    }, [index, current?.id]);

    const goPrev = useCallback(() => {
        if (!images || images.length === 0) return;
        const next = (index - 1 + images.length) % images.length;
        onIndexChange?.(next);
    }, [images, index, onIndexChange]);

    const goNext = useCallback(() => {
        if (!images || images.length === 0) return;
        const next = (index + 1) % images.length;
        onIndexChange?.(next);
    }, [images, index, onIndexChange]);

    // Keyboard navigation. Only when open so we don't trap shortcuts
    // when the viewer is closed.
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose?.();
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                goPrev();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                goNext();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onClose, goPrev, goNext]);

    // Body scroll lock while the viewer is open so iOS Safari doesn't
    // bounce the underlying gallery beneath the overlay.
    useEffect(() => {
        if (!open) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open]);

    if (!open || !current) return null;

    // Iter-154d — Resolve full-size src via the same helper used by
    // every other continuity-image view in the app. `entryImageSrc`
    // produces an absolute URL `${REACT_APP_BACKEND_URL}/.../file?...`
    // with the user's JWT appended; the backend's relative
    // `image_url` field is unusable as-is (no host, no token) which
    // was the root cause of the "Image unavailable" bug.
    const src =
        entryImageSrc(projectId, current, "display") ||
        entryImageSrc(projectId, current, "thumb") ||
        "";

    const onTouchStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        touchRef.current = { startX: t.clientX, startY: t.clientY };
    };
    const onTouchEnd = (e) => {
        const t = e.changedTouches?.[0];
        if (!t) return;
        const dx = t.clientX - touchRef.current.startX;
        const dy = t.clientY - touchRef.current.startY;
        // Horizontal swipe only — reject if the gesture is mostly vertical.
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) goNext();
        else goPrev();
    };

    // Iter-154e — Tappable links inside the metadata footer. The
    // viewer is open ABOVE the gallery; tapping a name closes the
    // viewer then routes to the relevant detail page with a returnTo
    // pointing back into the same Gallery URL.
    const goToCharacter = () => {
        if (!projectId || !current.character_id) return;
        const ret = galleryReturnTo?.();
        onClose?.();
        navigate(`${routeBase}/${projectId}/characters/${current.character_id}`, {
            state: ret ? { returnTo: ret } : undefined,
        });
    };
    const goToScene = () => {
        if (!projectId || !current.scene_id) return;
        const ret = galleryReturnTo?.();
        onClose?.();
        navigate(`${routeBase}/${projectId}/scenes/${current.scene_id}`, {
            state: ret ? { returnTo: ret } : undefined,
        });
    };

    return (
        <div
            data-testid="gallery-viewer"
            className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {/* Top bar — index + close. */}
            <div className="flex items-center justify-between px-4 py-3 text-neutral-300">
                <div
                    data-testid="gallery-viewer-index"
                    className="text-[11px] uppercase tracking-[0.25em] tabular-nums"
                >
                    {index + 1} / {images.length}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="gallery-viewer-close"
                    aria-label="Close viewer"
                    className="tap h-9 w-9 rounded-full hover:bg-white/10 inline-flex items-center justify-center"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Image stage. Click outside the image closes; click on
                the image is a no-op so the user can study it. */}
            <div
                className="flex-1 min-h-0 relative flex items-center justify-center px-2"
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose?.();
                }}
            >
                {src && !errored ? (
                    <img
                        data-testid="gallery-viewer-image"
                        src={src}
                        alt={current.state_label || ""}
                        onError={() => setErrored(true)}
                        className="max-h-full max-w-full object-contain select-none"
                        draggable={false}
                    />
                ) : (
                    <div
                        data-testid="gallery-viewer-missing"
                        className="text-center text-neutral-500 text-[12px] px-6"
                    >
                        Image unavailable.
                        <br />
                        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                            File may have been moved or removed
                        </span>
                    </div>
                )}

                {/* Edge prev/next chevrons — desktop affordance. The
                    primary mobile gesture is swipe; chevrons just give
                    cursor users a familiar hint. */}
                {images.length > 1 ? (
                    <>
                        <button
                            type="button"
                            onClick={goPrev}
                            data-testid="gallery-viewer-prev"
                            aria-label="Previous image"
                            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 hover:bg-black/60 text-white items-center justify-center"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            type="button"
                            onClick={goNext}
                            data-testid="gallery-viewer-next"
                            aria-label="Next image"
                            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 hover:bg-black/60 text-white items-center justify-center"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </>
                ) : null}
            </div>

            {/* Metadata footer — keep it shallow so the image stays the
                primary thing on screen. Iter-154e — character + scene
                are individually tappable; tapping closes the viewer
                and routes to the relevant detail page. */}
            <div className="px-4 py-3 border-t border-white/5 text-neutral-300">
                <div
                    data-testid="gallery-viewer-meta"
                    className="text-[12px] font-medium flex items-center gap-1.5 flex-wrap"
                >
                    {current.character_name ? (
                        <button
                            type="button"
                            data-testid="gallery-viewer-character-link"
                            onClick={goToCharacter}
                            className="tap underline-offset-2 hover:underline hover:text-white text-neutral-100"
                        >
                            {current.character_name}
                        </button>
                    ) : null}
                    {current.scene_episode || current.scene_number ? (
                        <>
                            {current.character_name ? (
                                <span className="text-neutral-600">·</span>
                            ) : null}
                            <button
                                type="button"
                                data-testid="gallery-viewer-scene-link"
                                onClick={goToScene}
                                className="tap underline-offset-2 hover:underline hover:text-white text-neutral-100"
                            >
                                {current.scene_episode
                                    ? `EP ${current.scene_episode}`
                                    : ""}
                                {current.scene_episode && current.scene_number ? " · " : ""}
                                {current.scene_number
                                    ? `Sc ${current.scene_number}`
                                    : ""}
                            </button>
                        </>
                    ) : null}
                    {current.look ? (
                        <>
                            <span className="text-neutral-600">·</span>
                            <span className="text-neutral-300">{current.look}</span>
                        </>
                    ) : null}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
                    {current.created_at ? formatRelativeTime(current.created_at) : ""}
                    {current.state_label && current.state_label !== "custom"
                        ? ` · ${current.state_label}`
                        : ""}
                </div>
            </div>
        </div>
    );
}
